import { useCallback, useEffect, useState } from 'react'
import Notiflix from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'

export const INTRODUCER_INDUSTRIES = [
    'Motor Finance',
    'Bailiff Misconduct',
    'Irresponsible Lending',
    'Medical Malpractice',
    'Military',
] as const

export type IntroducerIndustry = (typeof INTRODUCER_INDUSTRIES)[number]

export interface IntroducerApp {
    id: number
    display_name: string
    aws_app_name: string
    repo: string
    live_domain: string
    branch_name: string
    github_url: string
    industry: IntroducerIndustry
    active: boolean
}

export type IntroducerAppUpdate = Pick<IntroducerApp, 'id'> & Partial<Omit<IntroducerApp, 'id'>>

type RegistryErrorResponse = {
    message?: string
    details?: string
}

const showRegistryError = (response: RegistryErrorResponse | undefined, fallback: string) => {
    const message = response?.message || fallback
    const parts = [message]

    if (response?.details && response.details !== message) {
        parts.push(response.details)
    }
    Report.failure('Error', parts.join('\n\n'), 'OK')
}

export const useIntroducerRegistry = () => {
    const [apps, setApps] = useState<IntroducerApp[]>([])
    const [isLoading, setIsLoading] = useState(false)

    const loadApps = useCallback(async () => {
        setIsLoading(true)
        try {
            const res = await api.get('/introducer-registry')
            if (res.data?.success) {
                setApps(res.data.apps || [])
                return
            }
            showRegistryError(res.data, 'Failed to load introducer registry')
        } catch (err: any) {
            showRegistryError(err?.response?.data, 'Failed to load introducer registry')
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        void loadApps()
    }, [loadApps])

    const createApp = async (payload: Omit<IntroducerApp, 'id'>): Promise<boolean> => {
        try {
            const res = await api.post('/introducer-registry', payload)
            if (res.data?.success) {
                await loadApps()
                Notiflix.Notify.success('Added Successfully')
                return true
            }
            showRegistryError(res.data, 'Failed to create introducer app')
            return false
        } catch (err: any) {
            showRegistryError(err?.response?.data, 'Failed to create introducer app')
            return false
        }
    }

    const updateApp = async (payload: IntroducerAppUpdate): Promise<boolean> => {
        try {
            const res = await api.patch('/introducer-registry', payload)
            if (res.data?.success) {
                await loadApps()
                Notiflix.Notify.success('Updated Successfully')
                return true
            }
            showRegistryError(res.data, 'Failed to update introducer app')
            return false
        } catch (err: any) {
            showRegistryError(err?.response?.data, 'Failed to update introducer app')
            return false
        }
    }

    const deleteApp = async (id: number): Promise<boolean> => {
        try {
            const res = await api.delete('/introducer-registry', { data: { id } })
            if (res.data?.success) {
                await loadApps()
                Notiflix.Notify.success('Deleted Successfully')
                return true
            }
            showRegistryError(res.data, 'Failed to delete introducer app')
            return false
        } catch (err: any) {
            showRegistryError(err?.response?.data, 'Failed to delete introducer app')
            return false
        }
    }

    return {
        apps,
        isLoading,
        loadApps,
        createApp,
        updateApp,
        deleteApp,
    }
}
